import React, { useEffect, useMemo } from 'react';
import ComponentContainer from '../../components/component-container/ComponentContainer';
import { useHistory } from 'react-router-dom';
import routeConfig from '../../config/routeConfig';
import { Loader } from '../../components/loader/loader';
import Spinner from '../../components/spinner/spinner';
import { Certificate } from '../../entities/certificate';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import {
	QUERY_SIGNATURE_KEY,
	QUERY_SIGNATURE_NEW,
} from '../request-signature/PersonalInfo';
import { useAuthContext } from '@/components/auth-context/auth-context';
import { getUserName } from '@/utils/get-user-name';
import { useWindowDimensions } from '@/hooks/use-window-dimensions';
import { hasSeenTour, startTour } from '@/hooks/use-onboarding-tour';
import OnboardingProgressCard from './OnboardingProgressCard';

interface DashboardProps {
	certificates: Certificate[];
	isFetchingPersonalInformation: boolean;
}

const DESKTOP_TOUR_STEPS = [
	{
		element: '[data-tour="dashboard-welcome"]',
		popover: {
			title: 'Bienvenido a CyberSign',
			description:
				'Conoce como validar y firmar tus documentos dentro de nuestros app.',
		},
	},
	{
		element: '#cyber-sidenav',
		popover: {
			title: 'Accede facilmente a las herramientas',
			description:
				'En este menu puedes ver tus firmas, documentos y configuración.',
			side: 'right' as const,
		},
	},
	{
		element: '[data-tour="dashboard-signatures-btn"]',
		popover: {
			title: 'Empieza a firmar tus documentos',
			description:
				'Haz click aquí para crear tu certificado digital. Debes crear uno para poder firmar tus documentos.',
		},
	},
];

const MOBILE_TOUR_STEPS = DESKTOP_TOUR_STEPS.filter(
	(step) => step.element !== '[data-tour="dashboard-status"]',
);

const Dashboard: React.FC<DashboardProps> = (props) => {
	const history = useHistory();
	const { userProfile } = useAuthContext();
	const { isResponsive } = useWindowDimensions();
	const userName = getUserName(userProfile);
	const certificatesByStatus = useMemo(() => {
		return props.certificates.reduce<
			Partial<Record<Certificate['status'], number>>
		>(
			(numberByStatuses, certificate) => ({
				...numberByStatuses,
				[certificate.status]:
					(numberByStatuses[certificate.status] ?? 0) + 1,
			}),
			{},
		);
	}, [props.certificates]);

	useEffect(() => {
		if (props.isFetchingPersonalInformation) return;
		if (hasSeenTour('dashboard')) return;
		const steps = isResponsive ? MOBILE_TOUR_STEPS : DESKTOP_TOUR_STEPS;
		startTour('dashboard', steps);
	}, [props.isFetchingPersonalInformation, isResponsive]);

	return (
		<ComponentContainer>
			{props.isFetchingPersonalInformation ? (
				<div
					style={{
						width: '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<Spinner />
				</div>
			) : (
				<>
					{!userName ? (
						<h1 data-tour="dashboard-welcome">Hola</h1>
					) : (
						<h1
							data-tour="dashboard-welcome"
							style={{
								textTransform: 'capitalize',
								width: 'max-content',
								justifySelf: 'center',
							}}
						>
							Hola, {userName.split(' ')[0].toLowerCase()}
						</h1>
					)}

					<div className="cyber-spacer large" />

					<OnboardingProgressCard />

					<div data-tour="dashboard-status">
						{(certificatesByStatus.Pending ?? 0) > 0 ? (
							<>
								<Loader
									title="Generando tu primera firma"
									body="Este proceso toma unos minutos. Te notificaremos por correo cuando esté lista."
								/>
								<div className="cyber-spacer medium" />
							</>
						) : null}
						{
							// At least one signature
							// Object.values(JSON.parse(localStorage.getItem("signatures") || "{}")).length > 0 ? (
							//     <div className="cyber-alert">
							//         <div className="cyber-alert__image-container">
							//             <img src={imgSignature} alt="" />
							//         </div>
							//         <div className="cyber-alert__content">
							//             <h4 className="cyber-alert__title">Configura tu manuscrito</h4>
							//             <span>Te recomendamos hacerlo desde tu celular para poder hacer mejor tu manuscrito.</span>
							//         </div>
							//         <div className="cyber-alert__actions">
							//             <span className="cyber-button bordered" onClick={() => history.push(routeConfig.customManuscript)}>Configurar manuscrito</span>
							//         </div>
							//     </div>
							// ) : (
							//     <div className="cyber-alert">
							//         <div className="cyber-alert__image-container">
							//             <img src={imgSignature} alt="" />
							//         </div>
							//         <div className="cyber-alert__content">
							//             <h4 className="cyber-alert__title">Solicita tu firma</h4>
							//             <span>Debes iniciar la solicitud de firma para poder configurar tu manuscrito y comenzar a firmar.</span>
							//         </div>
							//         <div className="cyber-alert__actions">
							//             <button className="cyber-button bordered" onClick={() => history.push(routeConfig.requestSignature + `?${QUERY_SIGNATURE_KEY}=${QUERY_SIGNATURE_NEW}`)}>Solicita tu firma</button>
							//         </div>
							//     </div>
							// )
						}

						{/* <div className="cyber-spacer large" /> */}

						{(certificatesByStatus.Active ?? 0) > 0 ? (
							<div className="cyber-placeholder">
								<img
									className="cyber-placeholder__image"
									src={'/signed-document.svg'}
									alt=""
								/>
								<Button
									onClick={() =>
										history.push(routeConfig.signDocument)
									}
								>
									<Plus />
									Firmar nuevo documento
								</Button>
								<Button
									variant="outline"
									onClick={() =>
										history.push(routeConfig.signZip)
									}
									style={{ marginTop: 8 }}
								>
									<Plus />
									Firmar ZIP
								</Button>
							</div>
						) : null}
					</div>
					<div
						className="cyber-placeholder"
						data-tour="dashboard-signatures-btn"
					>
						<Button
							variant="outline"
							onClick={() =>
								history.push(routeConfig.mySignatures)
							}
							style={{ marginTop: 8 }}
						>
							<Plus />
							Mis firmas
						</Button>
					</div>
					{props.certificates.length === 0 ? (
						<div
							className="cyber-placeholder"
							style={{ padding: '5px' }}
						>
							<Button
								variant="outline"
								onClick={() =>
									history.push(
										routeConfig.requestSignature +
											`?${QUERY_SIGNATURE_KEY}=${QUERY_SIGNATURE_NEW}`,
									)
								}
							>
								<Plus />
								<span>Solicitar firma</span>
							</Button>
						</div>
					) : null}
				</>
			)}
		</ComponentContainer>
	);
};

export default Dashboard;
